import { getLogger } from '@confluence/logger';

import type {
	Principal,
	Role,
	PrincipalAndRole,
	UserPickerPrincipal,
	StagedPrincipalsAddMode,
	UserPickerUserPrincipal,
} from '../controlled/commonTypes';

import { type MasterStateSetter, type MasterState } from './masterState';
/**
 * Mutates the masterState to record that a single new principal should be
 * staged for addition
 */
export function stagePrincipalAddition(
	setMasterState: MasterStateSetter,
	principalToAdd: Principal,
	inputAddMode?: StagedPrincipalsAddMode,
) {
	setMasterState((masterState: MasterState) => {
		const {
			specificAccessDelta: { stagedAdditions, reassignments },
			addMode: defaultAddMode,
		} = masterState;
		const { id: principalId } = principalToAdd;
		const addMode = inputAddMode || defaultAddMode;
		const role: Role = addMode === 'as-editor' ? 'can-edit' : 'can-view';

		// Important: the state-hosted removals, stagedAdditions and reassignments objects
		// should be immutable. We have to build new ones to replace the originals.

		// If the principal is currently in the reassignments set, pull them out
		const newReassignments = new Map(reassignments);
		newReassignments.delete(principalId);

		// If the principal is currently in the stagedAdditions set, pull them out.
		const newAdditions = [
			{
				principal: principalToAdd,
				role,
			},
			...stagedAdditions.filter(
				({ principal: { id } }: PrincipalAndRole) => principalToAdd.id !== id,
			),
		];

		return {
			...masterState,
			addMode,
			specificAccessDelta: {
				...masterState.specificAccessDelta,
				stagedAdditions: newAdditions,
				reassignments: newReassignments,
			},
		};
	});
}

/**
 * A MasterState state-setter for staging the deltas for changing the role of a
 * Specific Access list principal
 */
export function stagePrincipalReassignment(
	setMasterState: MasterStateSetter,
	role: Role,
	id: string,
) {
	setMasterState((masterState: MasterState) => {
		const { specificAccessDelta } = masterState;

		// Otherwise, change the reassignments map
		{
			const { reassignments } = specificAccessDelta;

			const newReassignments = new Map(reassignments);
			newReassignments.set(id, role);

			return {
				...masterState,
				specificAccessDelta: {
					...specificAccessDelta,
					reassignments: newReassignments,
				},
			};
		}
	});
}

/**
 * A MasterState state-setter for staging the deltas for removing a Specific
 * Access list principal
 */
export function stagePrincipalRemoval(setMasterState: MasterStateSetter, id: string) {
	setMasterState((masterState: MasterState) => {
		const { specificAccessDelta } = masterState;

		// Otherwise, add the principal to the removals set, and if they're on the
		// reassignments map, remove them from that too
		{
			const { removals, reassignments } = specificAccessDelta;

			const newRemovals = new Set(removals);
			newRemovals.add(id);

			const newReassignments = new Map(reassignments);
			newReassignments.delete(id);

			return {
				...masterState,
				specificAccessDelta: {
					...specificAccessDelta,
					removals: newRemovals,
					reassignments: newReassignments,
				},
			};
		}
	});
}

/**
 * Filters the staged additions to ensure they only include principals
 * currently in the `principalsStagerPrincipals` list.
 */
export function filterStagedAdditionsByPrincipals(
	setMasterState: MasterStateSetter,
	principalsStagerPrincipals: UserPickerPrincipal[],
) {
	setMasterState((masterState: MasterState) => {
		const {
			specificAccessDelta: { stagedAdditions },
		} = masterState;

		// Extract IDs
		const principalsStagePrincipalIds = new Set(principalsStagerPrincipals.map((item) => item.id));

		// Filter stagedAdditions to include only items present in principalsStagePrincipals
		const newStagedAdditions = stagedAdditions.filter((item) =>
			principalsStagePrincipalIds.has(item.principal.id),
		);

		return {
			...masterState,
			specificAccessDelta: {
				...masterState.specificAccessDelta,
				stagedAdditions: newStagedAdditions,
			},
		};
	});
}

/**
 * Converts a principal from the format used by the platform user picker to
 * the one the USD uses internally, for the Specific Access section.
 */
export const convertUserPickerPrincipal = (platformPrincipal: UserPickerPrincipal): Principal => {
	const { id, name, type } = platformPrincipal;
	switch (type) {
		case 'user':
			return {
				type: 'user',
				id,
				name,
				avatarUrl: platformPrincipal.avatarUrl || '',
				email: platformPrincipal.email || '',
				knownErrors: (platformPrincipal as UserPickerUserPrincipal).knownErrors,
				sitePermissionType: platformPrincipal.isExternal ? 'EXTERNAL' : 'INTERNAL',
			};
		case 'group':
			return {
				type: 'group',
				id,
				name,
				usageType: null,
			};
		case 'email': {
			return {
				type: 'email',
				id,
				name,
			};
		}
		case 'team': {
			return {
				type: 'team',
				id,
				name,
				usageType: null,
				memberCount: platformPrincipal.memberCount,
				isVerified: platformPrincipal.verified || false,
				avatarSrc: platformPrincipal.avatarUrl || '',
			};
		}
		case 'external_user': {
			return {
				type: 'external_user',
				id,
				name,
				email: platformPrincipal.email || '',
				avatarUrl: platformPrincipal.avatarUrl || '',
			};
		}

		default:
			// This function is for converting principals that can appear in the
			// Specific Access table, and right now only users and groups can.
			const logger = getLogger('share-and-restrict-dialog:convertPrincipal');
			logger.error`Tried to convert an unsupported platform type: ${type}`;

			return {
				type: 'user',
				id,
				name,
				avatarUrl: '',
				email: '',
				knownErrors: undefined,
				sitePermissionType: '',
			};
	}
};
